import styled, { css } from "../../__compat__/styled-components/main.entry.mjs.mjs";
import curry from "../../__compat__/lodash/curry.mjs.mjs";
import { injectTypography } from "../../utils/injectTypography.mjs.mjs";
import { injectSpacing } from "../../api/spacing/injectSpacing.mjs.mjs";
import { injectOSXFontSmoothing } from "../../utils/injectOSXFontSmoothing.mjs.mjs";
const getStateColor = curry((statesFn, colorFn, props) => {
    const color = colorFn(props);
    const states = statesFn(props);
    if (props.disabled)
        return states.disabled || color;
    if (props.invalid)
        return states.invalid || color;
    return color;
});
const getSliderTrackColor = getStateColor((props) => ({
    disabled: props.theme.colors.ui.divider,
}));
const getSliderFillColor = getStateColor((props) => ({
    disabled: props.theme.colors.ui.highlight.secondary,
    invalid: props.theme.colors.ui.error,
}));
const getSliderThumbColor = getStateColor((props) => ({
    disabled: props.theme.colors.interaction.secondary.disabled,
    invalid: props.theme.colors.ui.error,
}));
const getOutputColor = getStateColor((props) => ({
    disabled: props.theme.colors.interaction.label.disabled,
    invalid: props.theme.colors.ui.error,
}));
const injectThumb = css `
  width: var(--thumb-diameter);
  height: var(--thumb-diameter);

  background-color: var(${(props) => props.theme.colors.background.level[1]});
  border-color: var(
    ${getSliderThumbColor((props) => props.theme.colors.interaction.secondary.default)}
  );
  border-style: solid;
  border-width: 1px;
  border-radius: 50%;
  box-shadow: none;

  appearance: none;
  pointer-events: auto;

  &:hover {
    border-color: var(
      ${getSliderThumbColor((props) => props.theme.colors.interaction.secondary.hover)}
    );
  }

  ${(props) => props.disabled
    ? css `
          cursor: not-allowed;
        `
    : css `
          cursor: grab;
          &:active {
            cursor: grabbing;
          }
        `}
`;
// When numberOfThumbs is 2 (dual slider) the container should be a `fieldset`,
// some styles here are a result of this.
export const StyledContainer = styled.div.withConfig({ displayName: "auir-611444a-StyledContainer", componentId: "auir-611444a-1krrdth" }) `
  margin: 0;
  padding: 0;

  border: 0;

  ${injectSpacing('margin')}
`;
export const StyledSliderContainer = styled.div.withConfig({ displayName: "auir-611444a-StyledSliderContainer", componentId: "auir-611444a-42utx4" }) `
  --range: calc(var(--max) - var(--min));

  --thumb-diameter: 26px;
  --thumb-radius: calc(var(--thumb-diameter) / 2);

  --output-size: 1ch;
  --track-size: 2px;

  --ratio0: calc((var(--value0) - var(--min)) / var(--range));
  --position0: calc(
    var(--thumb-radius) + (var(--ratio0) * (100% - var(--thumb-diameter)))
  );

  --slider-track-color: var(
    ${getSliderTrackColor((props) => props.theme.colors.ui.divider)}
  );
  --slider-track-fill-color: var(
    ${getSliderFillColor((props) => props.theme.colors.ui.highlight.secondary)}
  );

  ${(props) => props.numberOfThumbs === 2 &&
    css `
      --ratio1: calc((var(--value1) - var(--min)) / var(--range));
      --position1: calc(
        var(--thumb-radius) + (var(--ratio1) * (100% - var(--thumb-diameter)))
      );

      --midpoint: calc((var(--position0) + var(--position1)) / 2);
    `}

  display: grid;
  grid-template-rows:
    [ ${"output" /* GridRows.output */}] auto
    var(${(props) => props.theme.responsive.spacing.xs})
    [ ${"track" /* GridRows.track */}] auto
    var(${(props) => props.theme.responsive.spacing.xxs})
    [ ${"bounds" /* GridRows.bounds */}] auto;
  align-items: center;
`;
export const StyledSlider = styled.input.attrs({
    type: 'range',
}).withConfig({ displayName: "auir-611444a-StyledSlider", componentId: "auir-611444a-160mij6" }) `
  z-index: 1;

  grid-row: ${"track" /* GridRows.track */};
  grid-column: 1;
  width: 100%;
  height: var(--thumb-diameter);
  margin: 0;
  padding: 0;

  background: transparent;
  outline-offset: var(${(props) => props.theme.responsive.spacing.xxs});
  /* Prevent fade on iOS when disabled*/
  opacity: 1;

  appearance: none;

  pointer-events: none;

  &::-webkit-slider-thumb {
    ${injectThumb}
  }

  &:focus::-webkit-slider-thumb {
    border-color: var(
      ${getOutputColor((props) => props.theme.colors.interaction.secondary.focus)}
    );
  }

  &::-moz-range-thumb {
    ${injectThumb}
  }

  &:focus::-moz-range-thumb {
    border-color: var(
      ${getOutputColor((props) => props.theme.colors.interaction.secondary.focus)}
    );
  }

  &::-webkit-slider-track {
    pointer-events: none;
  }

  &::-moz-range-track {
    pointer-events: none;
  }
`;
export const StyledTrack = styled.div.withConfig({ displayName: "auir-611444a-StyledTrack", componentId: "auir-611444a-urm5o3" }) `
  --gradient-direction: to right;

  [dir='rtl'] & {
    --gradient-direction: to left;
  }

  position: relative;
  grid-row: ${"track" /* GridRows.track */};
  grid-column: 1;

  height: var(--track-size);

  &::before {
    content: '';
    z-index: 1;
    position: absolute;
    inset: 0;

    ${(props) => props.disabled &&
    css `
        opacity: 0.5;
      `}

    ${(props) => {
    switch (props.numberOfThumbs) {
        case 1:
            return css `
            background: linear-gradient(
              var(--gradient-direction),
              var(--slider-track-fill-color) var(--position0),
              var(--slider-track-color) var(--position0)
            );
          `;
        case 2:
            return css `
            background: linear-gradient(
              var(--gradient-direction),
              var(--slider-track-color) var(--start),
              var(--slider-track-fill-color) var(--start),
              var(--slider-track-fill-color) var(--end),
              var(--slider-track-color) var(--end)
            );
          `;
        default:
            return css `
            background-color: var(--slider-track-color);
          `;
    }
}}
  }

  ${(props) => props.marks.length &&
    css `
      &::after {
        content: '';
        z-index: 0;
        position: absolute;
        inset: -8px var(--thumb-radius);

        /* Using N gradients with one step here instead of one gradient with N steps due to a rendering bug in Chrome */
        background-image: ${props.marks
        .map(() => 'linear-gradient(to right, transparent, var(--slider-track-color) 0 1px)')
        .join(', ')};
        background-position-x: ${props.marks
        .map((value) => `calc(${value} / var(--range) * 100%)`)
        .join(', ')};
        background-repeat: no-repeat;
        background-size: 1px 100%;
      }
    `}
`;
export const StyledOutput = styled.output.withConfig({ displayName: "auir-611444a-StyledOutput", componentId: "auir-611444a-uk8j15" }) `
  display: inline-block;
  grid-row: ${"output" /* GridRows.output */};
  justify-self: center;

  font-variant-numeric: tabular-nums;

  color: var(
    ${getOutputColor((props) => props.theme.colors.interaction.label.default)}
  );

  ${StyledSlider}:focus ~ & {
    color: var(
      ${getOutputColor((props) => props.theme.colors.interaction.label.focus)}
    );
  }

  ${StyledSlider}:hover ~ & {
    color: var(
      ${getOutputColor((props) => props.theme.colors.interaction.label.hover)}
    );
  }

  ${(props) => injectTypography(props, 'copy1')}
  ${injectOSXFontSmoothing(true)}
  ${(props) => props.numberOfThumbs === 1 &&
    css `
      @supports (width: min(1px, 2px)) and (width: max(1px, 2px)) {
        --output-position: max(
          0px,
          min(
            calc(var(--position0) - (var(--output-size) / 2)),
            calc(100% - var(--output-size))
          )
        );

        justify-self: start;
        margin-inline-start: var(--output-position);
      }
    `}
`;
export const StyledMinMax = styled.div.withConfig({ displayName: "auir-611444a-StyledMinMax", componentId: "auir-611444a-jusrbh" }) `
  display: flex;
  grid-row: ${"bounds" /* GridRows.bounds */};
  justify-content: space-between;

  ${(props) => injectTypography(props, 'copy2')}
  ${injectOSXFontSmoothing(true)}

  color: var(${(props) => props.theme.colors.ui.tertiary});
`;
//# sourceMappingURL=Slider.styles.mjs.mjs.map